import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiExternalLink, FiMaximize2, FiPlay, FiVolume2, FiVolumeX,
} from 'react-icons/fi';
import ContentModal from '../ContentModal';
import { VIDEO_LABEL, isDirectVideo, parseVideo } from '../../utils/video';
import { claimPlayback, releasePlayback, useVideoSound } from '../../utils/videoSound';
import {
  releasePlayback as releaseMedia, requestPlayback, stopPlayback,
} from '../../utils/mediaPlayback';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

/** The same embed URL, but asking for sound (used only after a real user tap). */
const withSound = (src) => String(src || '').replace(/([?&])(mute|muted)=1/i, '$1$2=0');

/**
 * A video link inside a community article.
 *
 * · The card *follows the video*: a 16:9 link renders a 16:9 card, a 9:16 link
 *   (YouTube Shorts / Facebook Reel / TikTok / Instagram Reel) renders a 9:16
 *   card, always with a thumbnail while it is not playing.
 * · **Autoplay on scroll** — as soon as half of the card is on screen the real
 *   platform player is mounted with `autoplay=1` (YouTube / TikTok / Facebook /
 *   Instagram / Vimeo) so it looks and behaves like on that platform: its own
 *   controls, its own chrome. Scroll it away and the player is unmounted, so
 *   nothing keeps playing — or keeps downloading data — in the background.
 * · Browsers only allow a *muted* autoplay, so a “🔊” pill (and the speaker
 *   button) re-loads the embed with sound — that tap is the gesture the browser
 *   needs. The choice is shared with Reels + stories through `utils/videoSound`.
 * · `⤢` opens the same player full-screen, in the video's own ratio.
 */
export default function PostVideo({ url, title, showLabel = true }) {
  const { t } = useLanguage();
  const video = parseVideo(url);
  const direct = isDirectVideo(url);
  const embeddable = !!video.embed && video.type !== 'link';
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [embedPlaying, setEmbedPlaying] = useState(false);   // this embed owns the slot
  const [soundPref, setSoundPref] = useVideoSound();   // shared, ON by default
  const [unmuted, setUnmuted] = useState(false);       // this card really has sound
  const box = useRef(null);
  const media = useRef(null);

  // Autoplay when the card scrolls into view, stop when it scrolls away again.
  useEffect(() => {
    if (!direct && !embeddable) return undefined;
    const el = box.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);                     // very old browser: just play it
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting && e.intersectionRatio >= 0.5)),
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [direct, embeddable, url]);

  // One clip at a time across the site (shared with Reels + stories + mp3).
  useEffect(() => {
    if (!direct || !visible || open) return undefined;
    const el = media.current;
    if (!el) return undefined;
    claimPlayback(el);
    return () => releasePlayback(el);
  }, [direct, visible, open]);

  // An embed (YouTube / TikTok / Facebook / Instagram / Vimeo) cannot be paused
  // from here, so it takes the slot by handing over a **stop callback** that
  // swaps the iframe back to the preview — the platform player is unmounted and
  // goes silent. That is what keeps **one video playing at a time** while
  // scrolling, however many embed cards are in the feed.
  useEffect(() => {
    if (direct || !embeddable || !visible || open) {
      setEmbedPlaying(false);
      return undefined;
    }
    const stop = () => setEmbedPlaying(false);
    requestPlayback(stop, stop);
    setEmbedPlaying(true);
    return () => releaseMedia(stop);
  }, [direct, embeddable, visible, open]);

  const playUrl = video.autoplay || video.embed || '';
  // Inline: muted autoplay unless the reader tapped the speaker on this card.
  const playSrc = useMemo(() => (unmuted ? withSound(playUrl) : playUrl),
                              [playUrl, unmuted]);
  // Full screen is opened by a tap, so the shared preference can be honoured.
  const modalSrc = useMemo(
    () => ((soundPref || unmuted) ? withSound(playUrl) : playUrl),
    [playUrl, soundPref, unmuted],
  );

  if (!url) return null;

  // Anything we cannot embed (a random link) simply opens in a new tab.
  if (!direct && (video.type === 'link' || !video.embed)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        <FiExternalLink /> {url}
      </a>
    );
  }

  const portrait = video.portrait;
  const label = direct ? VIDEO_LABEL.link : (VIDEO_LABEL[video.type] || 'Video');
  const playing = visible && !open && (direct || embedPlaying);   // the player is mounted
  /** Full-screen: stop everything else first — nothing plays behind the modal. */
  const openModal = (e) => {
    if (e) e.stopPropagation();
    stopPlayback();
    setOpen(true);
  };
  const toggleSound = (e) => {
    e.stopPropagation();
    setUnmuted((on) => !on);
    if (!unmuted) setSoundPref(true);        // remember it for every other video
  };

  return (
    <>
      {/* The card — wrapper keeps a vertical video phone-sized and centred */}
      <div ref={box} className={portrait ? 'mt-2 flex justify-center' : 'mt-2'}>
        <div
          className="relative w-full rounded-xl overflow-hidden bg-gray-900 group"
          style={{ aspectRatio: video.ratio, maxWidth: portrait ? 320 : undefined }}
        >
          {playing ? (
            /* On screen → the real platform player, autoplaying, muted by default */
            direct ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                ref={media}
                src={url}
                autoPlay
                muted={!unmuted}
                loop
                playsInline
                controls
                onVolumeChange={(e) => {
                  const m = e.currentTarget.muted;
                  setUnmuted(!m);
                  if (!m) setSoundPref(true);
                }}
                className="absolute inset-0 w-full h-full object-contain bg-black"
              />
            ) : (
              <iframe
                title={title || label}
                src={playSrc}
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
              />
            )
          ) : (
            /* Off screen (or the modal is open) → back to the preview frame */
            <button
              type="button"
              onClick={openModal}
              className="absolute inset-0 w-full h-full"
              aria-label={title || label}
            >
              <Preview video={video} url={url} label={label} title={title} direct={direct} />

              <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
                <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-105 transition">
                  <FiPlay className="w-6 h-6 ml-0.5" style={{ color: BLUE }} />
                </span>
                {showLabel && (
                  <span className="text-white text-xs font-bold px-2 py-0.5 rounded-full bg-black/45">
                    {label} · {video.ratioKey}
                  </span>
                )}
              </span>
            </button>
          )}

          {/* Speaker (embeds only: a plain video has its own controls) + ⤢ */}
          <span className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
            {playing && !direct && (
              <button
                type="button"
                onClick={toggleSound}
                title={unmuted ? t('soundOff') : t('soundOn')}
                className="p-1.5 rounded-lg bg-black/55 text-white hover:bg-black/75"
              >
                {unmuted ? <FiVolume2 className="w-4 h-4" /> : <FiVolumeX className="w-4 h-4" />}
              </button>
            )}
            {!playing && (
              <span className="p-1.5 rounded-lg bg-black/45 text-white">
                <FiMaximize2 className="w-4 h-4" />
              </span>
            )}
          </span>

          {/* Muted autoplay is all a browser allows: one tap turns the sound on */}
          {playing && !direct && !unmuted && soundPref && (
            <button
              type="button"
              onClick={toggleSound}
              className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/60 text-white text-[11px] font-bold hover:bg-black/80"
            >
              <FiVolume2 className="w-3.5 h-3.5" /> {t('soundOn')}
            </button>
          )}
        </div>
      </div>

      {open && (
        <ContentModal title={title || ''} onClose={() => setOpen(false)} link={url}>
          <div className={video.portrait ? 'flex justify-center' : ''}>
            <div
              className="rounded-xl overflow-hidden bg-black w-full"
              style={{ aspectRatio: video.ratio, maxWidth: video.portrait ? 380 : undefined }}
            >
              {direct ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video src={url} controls autoPlay muted={!soundPref} playsInline className="w-full h-full object-contain" />
              ) : (
                <iframe
                  title={title || 'video'}
                  src={modalSrc}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                />
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {label} · {video.id || url}
          </p>
        </ContentModal>
      )}
    </>
  );
}

/** Thumbnail (or the platform's own poster frame) shown on the card. */
function Preview({ video, url, label, title, direct }) {
  const [broken, setBroken] = useState(false);

  if (direct) {
    // A plain video file: the browser shows its own first frame as the poster.
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={url}
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  if (video.preview === 'iframe' && video.embed && !video.autoplay) {
    // Facebook / Instagram / TikTok give us their own thumbnail through the
    // player — scaled a bit so the platform chrome is not in the way.
    return (
      <span className="absolute inset-0 overflow-hidden pointer-events-none bg-black">
        <iframe
          title={`${label} preview`}
          src={video.embed}
          loading="lazy"
          scrolling="no"
          className="absolute border-0"
          style={{
            top: '50%',
            left: '50%',
            width: '160%',
            height: '160%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
      </span>
    );
  }

  if (video.thumb && !broken) {
    return (
      <img
        src={video.thumb}
        alt={title || label}
        loading="lazy"
        onError={() => {
          if (video.thumbFallback) setBroken(true);
        }}
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  if (video.thumbFallback) {
    return (
      <img
        src={video.thumbFallback}
        alt={title || label}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  return <span className="absolute inset-0 bg-gradient-to-br from-[#0B5FDB] to-[#083E93]" />;
}
